/**
 * A member's front page.
 *
 * ---------------------------------------------------------------------
 * WHAT THIS REPLACES
 *
 * The front door for anybody below staff was `/golf/my` — a golf page,
 * shown to every tennis member, every swimmer and everybody who keeps a
 * boat, because it was the first member page written. A club whose pool
 * is its busiest amenity opened onto a tee sheet.
 *
 * ---------------------------------------------------------------------
 * THE NEXT THING IS THE WHOLE PAGE
 *
 * A member opening this has one question and it is almost never "show me
 * my two weeks". It is "is my court at four or half past", asked in a
 * car park, on a phone, five minutes before they need the answer.
 *
 * So the next thing is a sentence, before any list: "Singles — Court 4,
 * Saturday at 4:00, in about an hour." The two weeks is underneath, for
 * the smaller number of times somebody is actually planning.
 *
 * ---------------------------------------------------------------------
 * `on_date` AND `local_time`, NOT THE INSTANT
 *
 * 0198 carries three columns for one moment: `starts_at` as a real
 * instant, and `on_date` / `local_time` as what the sheet says. The
 * instant is for ordering and for "in about an hour". Everything
 * PRINTED comes from the other two.
 *
 * That is the difference between this page being right and being
 * subtly wrong for the people least able to check it. A member reading
 * this in a hotel in another country must see the club's Saturday, and
 * `new Date(starts_at).toLocaleDateString()` would show them their own.
 * The database already did the timezone work; re-deriving the date here
 * would throw it away one function call from the screen.
 *
 * The one place the instant is used for display is the "in about an
 * hour" clause, which is a duration and so is the same number wherever
 * the reader is standing.
 *
 * ---------------------------------------------------------------------
 * DAYS, NOT ROWS
 *
 * A table with a date column repeated down it is a report. A member
 * reads their week as days with things in them, so the flat ordered list
 * is grouped on `on_date` — which needs no timezone reasoning at all,
 * being already the club's own calendar day.
 */

import { useMemo } from 'react';
import { useApi } from '../lib/useApi.ts';
import { money, date as fmtDate } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { Card, Empty, ErrorBanner, Loading, Status } from '../components/ui.tsx';

interface Booking {
  module: string;
  kind: string;
  booking_id: string;
  row_key: string;
  starts_at: string;
  ends_at: string | null;
  on_date: string;
  local_time: string;
  where_at: string | null;
  what: string;
  status: string;
  is_live: boolean;
  party: string | null;
}

interface HouseholdRow {
  member_account_id: string;
  account_number: string;
  membership_number: string;
  membership_status: string;
  category_name: string;
  membership_person_id: string | null;
  person_id: string | null;
  display_name: string | null;
  role: string | null;
  is_current: boolean | null;
}

interface Standing {
  member_account_id: string;
  account_number: string;
  balance_minor: string;
  bucket_90_plus_minor: string;
}

interface Charge {
  ar_transaction_id: string;
  accounting_date: string;
  txn_type: string;
  description: string | null;
  amount_minor: string;
  outlet_name: string | null;
}

interface Notice {
  alert_id: string;
  message: string;
  course_name: string | null;
}

interface HomePayload {
  is_member: boolean;
  week: Booking[];
  household: HouseholdRow[];
  standing: Standing[];
  charges: Charge[];
  notices: Notice[];
}

/**
 * Where each module's own screen lives.
 *
 * The keys are `v_member_booking`'s five module names, exactly. Three of
 * the addresses were wrong when this file was written — `/courts/my`,
 * `/pool/my` and `/range/my`, none of which existed — and every one of
 * them was a link a member could see and tap on their own front page.
 * Nothing failed: React Router's catch-all sent them back to this same
 * page, so tapping "racquet" beside a court booking silently reloaded
 * the home screen, which reads as the software ignoring them.
 *
 * A wrong address here has no failing test anywhere, because the route
 * table and this map are two lists nothing compares. They are checked
 * against `App.tsx` by eye, and the way to keep them honest is to add
 * the module's link in the same commit as the module's page.
 */
const MODULE_HOME: Record<string, string> = {
  golf: '/golf/my',
  racquet: '/tennis/my-courts',
  aquatics: '/pool/my',
  boating: '/boating/my',
  shooting: '/shooting/my',
};

/** "16:00:00" -> "4:00 PM", without going near a Date. */
function clockTime(local: string, locale?: string): string {
  const [h, m] = local.split(':');
  const d = new Date(2000, 0, 1, Number(h), Number(m));
  return d.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' });
}

/** "2026-09-12" -> "Saturday, September 12", the club's day, no zone maths. */
function longDate(isoDate: string, locale?: string): string {
  const [y, m, d] = isoDate.split('-').map(Number);
  return new Date(y!, m! - 1, d!).toLocaleDateString(locale, {
    weekday: 'long', month: 'long', day: 'numeric',
  });
}

export function HomePage() {
  const { currency, club } = useSession();
  const home = useApi<HomePayload>('/v1/me/home');

  const locale = club?.default_locale ?? undefined;
  const week = home.data?.week ?? [];

  const days = useMemo(() => {
    const out: Array<{ key: string; label: string; items: Booking[] }> = [];
    for (const b of week) {
      const last = out[out.length - 1];
      if (last && last.key === b.on_date) last.items.push(b);
      else out.push({ key: b.on_date, label: longDate(b.on_date, locale), items: [b] });
    }
    return out;
  }, [week, locale]);

  // The first thing that has not finished yet — not the first thing in
  // the list. Somebody opening this at half past four does not want to
  // be told about the court they are standing on.
  const next = useMemo(() => {
    const now = Date.now();
    return week.find((b) => new Date(b.ends_at ?? b.starts_at).getTime() >= now) ?? null;
  }, [week]);

  if (home.loading && !home.data) return <Loading />;

  const standing = home.data?.standing ?? [];
  const owed = standing.reduce((n, s) => n + Number(s.balance_minor), 0);
  const veryLate = standing.reduce((n, s) => n + Number(s.bucket_90_plus_minor ?? 0), 0);
  const household = (home.data?.household ?? []).filter((h) => h.is_current !== false);

  return (
    <>
      <h1>{club?.brand_name ?? 'The club'}</h1>
      <ErrorBanner error={home.error} />

      {/* Above everything, including the next thing on: a member whose
          Saturday is cancelled should not read the tee time first. */}
      {(home.data?.notices ?? []).map((n) => (
        <div key={n.alert_id} className="banner banner--warning">
          <strong>{n.course_name ?? club?.brand_name}</strong> — {n.message}
        </div>
      ))}

      {home.data?.is_member === false ? (
        <Card title="Nothing Here Yet">
          <Empty>
            This login is not attached to a membership, so there is no week to show. The club
            office can link it.
          </Empty>
        </Card>
      ) : (
        <>
          {next ? <NextUp item={next} locale={locale} /> : (
            <Card title="Nothing Booked">
              <Empty>
                Nothing on in the next two weeks. The tee sheet, the courts and the pool are all
                bookable from the menu.
              </Empty>
            </Card>
          )}

          {/* ======================================================== */}
          {owed !== 0 && (
            <Card title="The Account">
              <div className="tiles">
                {standing.map((s) => (
                  <div key={s.member_account_id} className="tile">
                    <div className="tile__label">
                      {standing.length > 1 ? s.account_number : 'Balance'}
                    </div>
                    <div className="tile__value">{money(Number(s.balance_minor), currency)}</div>
                    <div className="tile__note">
                      {Number(s.bucket_90_plus_minor) > 0
                        ? <Status tone="warning">some over 90 days</Status>
                        : 'up to date'}
                    </div>
                  </div>
                ))}
              </div>
              {veryLate > 0 && (
                <p className="muted small">
                  {money(veryLate, currency)} has been outstanding more than ninety days.
                  Statements and payment methods are under the account menu.
                </p>
              )}
            </Card>
          )}

          {/* ======================================================== */}
          <Card title="The Next Two Weeks">
            {days.length === 0 ? (
              <Empty>Nothing booked.</Empty>
            ) : (
              days.map((d) => (
                <div key={d.key} className="agenda-day">
                  <h3 className="agenda-day__label">{d.label}</h3>
                  <ul className="agenda">
                    {d.items.map((b) => (
                      <li key={b.row_key} className="agenda__item">
                        <span className="agenda__time">{clockTime(b.local_time, locale)}</span>
                        <span className="agenda__what">
                          <strong>{b.what}</strong>
                          {b.where_at ? <span className="muted"> · {b.where_at}</span> : null}
                          {/* Who else is on it. 0198 collapses a group to
                              one row and names it here, which is the
                              difference between "18 holes" and "18 holes
                              with the people you asked". */}
                          {b.party ? <div className="muted small">{b.party}</div> : null}
                        </span>
                        <span className="agenda__module">
                          {MODULE_HOME[b.module]
                            ? <a href={MODULE_HOME[b.module]}>{b.module}</a>
                            : <span className="muted small">{b.module}</span>}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))
            )}
          </Card>

          {/* ======================================================== */}
          {(home.data?.charges.length ?? 0) > 0 && (
            <Card title="Recently Charged">
              <div className="table">
                <table className="data data--cards">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>What</th>
                      <th>Where</th>
                      <th className="num">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(home.data?.charges ?? []).map((c) => (
                      <tr key={c.ar_transaction_id}>
                        <td data-label="Date" className="muted small">{fmtDate(c.accounting_date)}</td>
                        <td data-label="What" className="cell--title">
                          {c.description ?? c.txn_type}
                        </td>
                        <td data-label="Where" className="muted small">{c.outlet_name ?? '—'}</td>
                        <td data-label="Amount" className="num">
                          {money(Number(c.amount_minor), currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* ======================================================== */}
          {household.length > 1 && (
            <Card title="On the Membership">
              <p className="muted small">
                Everything above covers the whole household — a court your spouse booked is on
                your week too, because it is on the same membership.
              </p>
              <ul className="agenda">
                {household.map((h) => (
                  <li key={h.membership_person_id ?? h.person_id ?? h.account_number}
                      className="agenda__item">
                    <span className="agenda__what">
                      {h.display_name ?? '—'}
                      {h.role ? <span className="muted"> · {h.role}</span> : null}
                    </span>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </>
      )}
    </>
  );
}

/**
 * The one line the page exists for.
 *
 * A sentence, not a row in a table. "Singles — Court 4, Saturday at
 * 4:00, in about an hour" answers the question somebody actually has; a
 * well-formatted table of their two weeks makes them find it themselves.
 */
function NextUp({ item, locale }: { item: Booking; locale?: string }) {
  const mins = Math.round((new Date(item.starts_at).getTime() - Date.now()) / 60000);

  // Rounded on purpose. "In about an hour" is what somebody needs; "in
  // 58 minutes" is a number they then have to do something with, and it
  // is wrong by the time they have read it.
  const when =
    mins < 0 ? 'on now'
      : mins < 5 ? 'about to start'
      : mins < 60 ? `in ${mins} minutes`
      : mins < 120 ? 'in about an hour'
      : mins < 60 * 10 ? `in about ${Math.round(mins / 60)} hours`
      : null;

  return (
    <Card title="Next">
      <p className="next-up">
        <strong>{item.what}</strong>
        {item.where_at ? ` — ${item.where_at}` : ''}
        {', '}
        {longDate(item.on_date, locale)} at {clockTime(item.local_time, locale)}
        {when ? <span className="muted">{`, ${when}`}</span> : null}
      </p>
      {item.party && <p className="muted small">With {item.party}.</p>}
    </Card>
  );
}
